import { GetStaticProps, NextPage } from 'next'
import dynamic from 'next/dynamic'
import React from 'react'
// import { EventDetailContainer } from 'src/components/EventDetailContainer/EventDetailContainer'
import { EventDetailHeader } from 'src/components/EventDetailHeader/EventDetailHeader'
import { Footer } from 'src/components/Footer/Footer'
// import { Breadcrumb } from 'src/components/global/Breadcrumb'
import MainContainer from 'src/components/global/MainContainer/MainContainer'
import { ThemeOnly } from 'src/components/global/ThemeOnly'
import SkeletonLoading from 'src/components/SkeletonLoading/SkeletonLoading'
import { THEME } from 'src/config/config'
import { apolloSdk } from 'src/graphql/apolloSdk'
import { IEvent } from 'src/graphql/generated/hooks'
import { Meta } from 'src/layout/Meta'
import { Main } from 'src/templates/Main'
import { getVendorDisplayName } from 'src/utils/getVendorDisplayName'
import { withRetries } from 'src/utils/retryHelper'

type Props = {
  event: IEvent
}

const EventDetailContainer = dynamic(
  () => import('src/components/EventDetailContainer/EventDetailContainer').then((mod) => mod.default),
  {
    loading: () => <SkeletonLoading className="bg-navy" />,
  },
)

const Breadcrumb = dynamic(() => import('src/components/global/Breadcrumb').then((mod) => mod.Breadcrumb), {
  loading: () => <SkeletonLoading className="bg-navy" />,
})

const EventPage: NextPage<Props> = ({ event }: Props) => {
  const title = event?.title || ''
  const organizer = event?.organizer || ''
  const description = event?.description || ''
  const country = event?.country || ''
  const city = event?.city || ''
  const slug = event?.slug || ''
  const imageUrl = event?.logo?.url

  const location = `${country} ${city}`
  const displayName = getVendorDisplayName(organizer, title)

  const meta = (
    <Meta
      title={displayName}
      description={description}
      url={`/event/${slug}/`}
      image={imageUrl}
      keywords={[organizer, location]}
    />
  )
  return (
    <Main meta={meta}>
      <MainContainer id="mainContainer">
        <div className="flex flex-col bg-clouds">
          <Breadcrumb subject="events" subtitle={title} subjectRoute="/events/" />
          <EventDetailHeader event={event} />
        </div>
        <hr />
        <div className="flex flex-row justify-center w-full">
          <div className="!max-w-[1100px] flex flex-row justify-between w-full mb-32">
            <EventDetailContainer event={event} />
          </div>
        </div>
        <ThemeOnly theme="legaltech">
          <Footer />
        </ThemeOnly>
      </MainContainer>
    </Main>
  )
}

export default EventPage

export const getStaticProps: GetStaticProps = async ({ params }) => {
  if (!params?.slug || THEME === 'ilta') {
    return {
      revalidate: 60,
      notFound: true,
    }
  }
  try {
    const slug = params.slug as string
    // const { slugUpdates } = await apolloSdk.getSlugUpdates({ type: 'event', oldSlug: slug })
    // if (slugUpdates && slugUpdates?.data.length > 0) {
    //   slug = slugUpdates.data[0].attributes?.newSlug || slug
    // }
    const { events } = (await withRetries(() => apolloSdk.getEventsBySlug({ slug }))) || []
    const event = events && events.length > 0 ? events[0] : null
    if (!event) {
      return {
        revalidate: 60,
        notFound: true,
      }
    }
    return {
      props: {
        event,
      },
      revalidate: 60, // Only cache successful pages
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error fetching event:', error)
    return {
      notFound: true,
      revalidate: 5,
    }
  }
}

export async function getStaticPaths() {
  const { events } = (await apolloSdk.getEventsByOffset({ start: 0, limit: 10 })) || []
  const paths: Array<{ params: { slug: string } }> = []

  if (events) {
    events.forEach((item) => {
      if (!item) return

      paths.push({ params: { slug: item?.slug || '' } })
    })
  }

  return {
    paths,
    fallback: 'blocking',
  }
}
